import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetCountriesListQuery } from 'services/assets';
import { useGetMerchantQuery, useLazyGetMerchantsListQuery } from 'services/merchants';
import { MerchantStatus, MerchantsListQuery } from 'services/merchants/types';

import { SelectItem } from 'components/inputs/select/select';

import { LoadingScreen } from 'pages/loading/loading';

import selectAccount from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountType } from 'types/account';
import { RequestPagination, ServerPagination } from 'types/pagination';
import { UserAccessLevel } from 'types/user';

import CryptoPayoutCreate from '.';

export const CryptoPayoutContainer = () => {
  const { account } = useSelector(selectAccount);

  const userLevel = useSelector(selectUserLevel);
  const isSelectAccountPSPOrGA = [AccountType.PSP, AccountType.Pwc_Group].includes(account.type);
  const canCreateOrdersForDifferentMerchants = isSelectAccountPSPOrGA || Boolean(userLevel && userLevel > UserAccessLevel.Support);
  const { isLoading: isMerchantInfoLoading, data: merchantInfo } = useGetMerchantQuery(account.uuid, {
    skip: !account.uuid || canCreateOrdersForDifferentMerchants,
  });

  const [fetchMerchants] = useLazyGetMerchantsListQuery();
  const { data: countries = [] } = useGetCountriesListQuery();

  const residentialCountries: SelectItem[] = useMemo(
    () =>
      countries
        .filter((country) => country.bridgeRiskClassification === 'NotHighRisk' || country.bridgeRiskClassification === 'Restricted')
        .map((country) => ({
          key: country.isoAlpha3,
          icon: country.iconUrl,
          label: country.name,
        })),
    [countries],
  );

  const getActiveMerchants = async (params: {
    search?: string;
    pagination?: RequestPagination;
  }): Promise<{ data: SelectItem[]; pagination?: ServerPagination }> => {
    const query: MerchantsListQuery = {
      pagination: params.pagination,
      filters: {
        status: MerchantStatus.Active,
        cryptoPayoutEnabled: true,
        ...(userLevel === UserAccessLevel.Partner && account.uuid ? { accountUuids: [account.uuid] } : {}),
      },
    };

    if (params.search) {
      query.search = params.search;
    }

    const response = await fetchMerchants(query);

    if ('data' in response) {
      const records: SelectItem[] = response.data!.data.map((item) => ({
        key: item.uuid,
        label: item.name,
        value: item,
      }));

      return {
        data: records,
        pagination: response.data!.pagination,
      };
    }

    return { data: [] };
  };

  if (isMerchantInfoLoading) {
    return <LoadingScreen />;
  }

  return (
    <CryptoPayoutCreate
      merchant={merchantInfo?.data}
      residentialCountries={residentialCountries}
      getMerchants={getActiveMerchants}
      canCreateOrdersForDifferentMerchants={canCreateOrdersForDifferentMerchants}
      readOnlyMode={false}
    />
  );
};
